<script setup lang="ts">
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import InputSwitch from "primevue/inputswitch";
import RadioButton from "primevue/radiobutton";
import { ref } from "vue";
import { hasEnableContentMangementEnabled } from "@/composables/authorization.js";
import ConfirmationModal from "./ContentManagementConfirmationModal.vue";
import { dialogPt, inputSwitchPt } from "./style.ts";

const props = defineProps<{
  onResetDecision: (decision: boolean) => void;
}>();

const excludePublicContent = defineModel("excludePublicContent");
const aiAdvisingEnabled = defineModel("aiAdvisingEnabled", { type: Boolean });
const specialPricingFilterEnabled = defineModel("specialPricingFilterEnabled", { type: Boolean });

const showInclusiveChoiceModal = ref(false);
const showExclusiveChoiceModal = ref(false);
const showInclusiveEraseConfirmationModal = ref(false);
const showExclusiveEraseConfirmationModal = ref(false);

const selectInclusiveOption = () => {
  if (excludePublicContent.value) {
    showInclusiveChoiceModal.value = true;
  }
};

const handleInclusiveChoice = (eraseSettings: boolean) => {
  showInclusiveChoiceModal.value = false;
  if (eraseSettings) {
    showInclusiveEraseConfirmationModal.value = true;
  } else {
    excludePublicContent.value = false;
    props.onResetDecision(false);
  }
};

const confirmInclusiveErase = () => {
  showInclusiveEraseConfirmationModal.value = false;
  excludePublicContent.value = false;
  props.onResetDecision(true);
};

const cancelInclusiveErase = () => {
  showInclusiveEraseConfirmationModal.value = false;
};

const selectExclusiveOption = () => {
  if (!excludePublicContent.value) {
    showExclusiveChoiceModal.value = true;
  }
};

const handleExclusiveChoice = (eraseSettings: boolean) => {
  showExclusiveChoiceModal.value = false;
  if (eraseSettings) {
    showExclusiveEraseConfirmationModal.value = true;
  } else {
    excludePublicContent.value = true;
    props.onResetDecision(false);
  }
};

const confirmExclusiveErase = () => {
  showExclusiveEraseConfirmationModal.value = false;
  excludePublicContent.value = true;
  props.onResetDecision(true);
};

const cancelExclusiveErase = () => {
  showExclusiveEraseConfirmationModal.value = false;
};
</script>

<template>
  <div v-if="hasEnableContentMangementEnabled">
    <h1>Choose how you want to control which content appears for learners.</h1>
    <div class="card flex justify-left">
      <div class="flex flex-col gap-4 py-2">
        <div class="flex items-center gap-2">
          <RadioButton
            :modelValue="excludePublicContent"
            inputId="Inclusive"
            name="contentManagement"
            :value="false"
            @click="selectInclusiveOption"
          />
          <label for="Inclusive">Inclusive - Show all content by default. Learners have access to everything unless manually hidden.</label>
        </div>
        <div class="flex items-center gap-2">
          <RadioButton
            :modelValue="excludePublicContent"
            inputId="Exclusive"
            name="contentManagement"
            :value="true"
            @click="selectExclusiveOption"
          />
          <label for="Exclusive">Exclusive - Only show selected content. Learners will see only what you explicitly approve.</label>
        </div>
      </div>
    </div>

    <Dialog v-model:visible="showInclusiveChoiceModal" modal header="Apply Inclusive to existing content?" :pt="dialogPt">
      <p>This will change how content visibility is managed. You can erase existing 'Exclusive' rules or keep them for future use.</p>
      <div class="flex justify-end gap-2 mt-4">
        <Button label="Apply to future content only" @click="handleInclusiveChoice(false)" />
        <Button label="Erase current settings" @click="handleInclusiveChoice(true)" severity="danger" />
      </div>
    </Dialog>

    <ConfirmationModal
      v-model:visible="showInclusiveEraseConfirmationModal"
      title="Erase Current Exclusive Settings?"
      message="This will permanently delete your existing 'Exclusive' visibility rules. Are you absolutely sure?"
      @confirm="confirmInclusiveErase"
      @cancel="cancelInclusiveErase"
    />

    <Dialog v-model:visible="showExclusiveChoiceModal" modal header="Apply Exclusive to existing content?" :pt="dialogPt">
      <p>Existing content will remain visible unless you choose otherwise.</p>
      <div class="flex justify-end gap-2 mt-4">
        <Button label="Apply to future content only" @click="handleExclusiveChoice(false)" />
        <Button label="Erase current settings" @click="handleExclusiveChoice(true)" severity="danger" />
      </div>
    </Dialog>

    <ConfirmationModal
      v-model:visible="showExclusiveEraseConfirmationModal"
      title="Erase Current Settings"
      message="This selection can impact any setup you might have already done. Are you sure that you want to erase the current settings for your Exclusive selection?"
      @confirm="confirmExclusiveErase"
      @cancel="cancelExclusiveErase"
    />
  </div>
    <div class="flex flex-row gap-1 mt-4">
      <InputSwitch
      v-model="aiAdvisingEnabled"
      :pt="inputSwitchPt"
      data-testid="aiAdvisingSwitch"
      />
      <span>Enable AI-supported Advising</span>
      <span v-tooltip="'When enabled, students receive automated advising support powered by AI directly on your storefront. When disabled, students are routed through standard support channels (e.g. ticketing or contact forms).'" class="material-symbols-outlined">help</span>
  </div>
    <div class="flex flex-row gap-1 mt-4">
      <InputSwitch
      v-model="specialPricingFilterEnabled"
      :pt="inputSwitchPt"
      data-testid="specialPricingFilterSwitch"
      />
      <span>Enable special pricing filter</span>
      <span v-tooltip="`Show this organization's special pricing filter on its storefront search page.`" class="material-symbols-outlined">help</span>
  </div>
</template>
